import React, { useMemo, useState } from "react";

const DEFAULT_LABELS = {
  filterBy: "Filter results by:",
  resetFilter: "Reset Filter",
  invoiceTypes: "Invoice Types",
  chargeType: "Charge Type",
  find: "Find",
  results: "Results",
  shortCode: "Short Code",
  title: "Title",
  description: "Description",
  parentInvoiceType: "Parent Invoice Type",
  taxExempt: "Tax Exempt",
  emptyState: "Sorry, there are no results.",
};

function renderHtml(html) {
  return { __html: html || "" };
}

function scrollToResults() {
  if (window.jQuery) {
    const grid = window.jQuery("#invoiceChargeGrid");

    if (grid.length) {
      window.jQuery("html, body").animate({ scrollTop: grid.offset().top }, 300);
      return;
    }
  }

  const element = document.getElementById("invoiceChargeGrid");

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function DropdownButton({ id, disabled, className, contentHtml }) {
  return (
    <button
      className={className}
      type="button"
      id={id}
      data-bs-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
      disabled={disabled}
      dangerouslySetInnerHTML={renderHtml(contentHtml)}
    />
  );
}

export default function InvoiceChargeTypeIsland({ config }) {
  const labels = { ...DEFAULT_LABELS, ...(config.labels || {}) };
  const invoiceTypes = Array.isArray(config.invoiceTypes) ? config.invoiceTypes : [];
  const chargeTypes = Array.isArray(config.chargeTypes) ? config.chargeTypes : [];

  const [selectedInvoiceType, setSelectedInvoiceType] = useState(null);
  const [selectedChargeType, setSelectedChargeType] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const filteredChargeTypes = useMemo(() => {
    if (!selectedInvoiceType) {
      return null;
    }

    return chargeTypes.filter((item) => item.ParentInvoiceTypeId === selectedInvoiceType.Id);
  }, [chargeTypes, selectedInvoiceType]);

  const canFind = !!selectedInvoiceType && !!selectedChargeType && !showResults;
  const hasResults = !!selectedChargeType;

  return (
    <div className="container" id="invoiceChargeGrid">
      <div className="bg-lightest-blue p-3 rounded-xl">
        <div className="d-flex flex-wrap">
          <div className="h4">{labels.filterBy}</div>
          <button
            className="btn btn-link p-0 align-self-start ml-auto"
            type="button"
            onClick={() => {
              setSelectedInvoiceType(null);
              setSelectedChargeType(null);
              setShowResults(false);
            }}
          >
            <u>{labels.resetFilter}</u>
          </button>
        </div>
        <div className="row mb-lg-3">
          <div className="col-lg-4 col-md-6 mt-3 align-self-end">
            <div className="label pb-2">{labels.invoiceTypes}</div>
            <div className="dropdown">
              <DropdownButton
                id="invoiceTypesDropdown"
                className="btn-dropdown dropdown-toggle pr-0"
                disabled={false}
                contentHtml={
                  selectedInvoiceType ? selectedInvoiceType.ShortCode : config.placeholderInvoiceType
                }
              />
              <div className="dropdown-menu" aria-labelledby="invoiceTypesDropdown">
                {invoiceTypes.map((item) => (
                  <button
                    key={item.Id}
                    className={`dropdown-item ${selectedInvoiceType && item.Id === selectedInvoiceType.Id ? "active" : ""}`}
                    type="button"
                    onClick={() => {
                      setSelectedInvoiceType(item);
                      setSelectedChargeType(null);
                      setShowResults(false);
                    }}
                  >
                    {item.ShortCode}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mt-3 align-self-end">
            <div className="label pb-2">{labels.chargeType}</div>
            <div className="dropdown">
              <DropdownButton
                id="chargeTypesDropdown"
                className={`btn-dropdown dropdown-toggle pr-0 ${filteredChargeTypes ? "" : "disabled"}`.trim()}
                disabled={!filteredChargeTypes}
                contentHtml={
                  selectedChargeType ? selectedChargeType.ShortCode : config.placeholderChargeType
                }
              />
              <div className="dropdown-menu" aria-labelledby="chargeTypesDropdown">
                {(filteredChargeTypes || []).map((item) => (
                  <button
                    key={item.Id}
                    className={`dropdown-item ${selectedChargeType && item.Id === selectedChargeType.Id ? "active" : ""}`}
                    type="button"
                    onClick={() => {
                      if (!selectedChargeType || item.Id !== selectedChargeType.Id) {
                        setSelectedChargeType(item);
                        setShowResults(false);
                      }
                    }}
                  >
                    {item.ShortCode}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mt-3 d-flex">
            {canFind ? (
              <button
                className="btn btn-primary mt-auto align-self-end"
                type="button"
                onClick={() => {
                  setShowResults(true);
                  scrollToResults();
                }}
              >
                {labels.find}
              </button>
            ) : (
              <span
                className="disabled btn btn-primary mt-auto align-self-end"
                style={{ backgroundColor: "#405ba3" }}
              >
                {labels.find}
              </span>
            )}
          </div>
        </div>
      </div>
      {showResults ? (
        <div className="resultsTable mt-4">
          <div className="h3">{labels.results}</div>
          {hasResults ? (
            <div>
              <div>
                <h5>
                  {selectedInvoiceType.Title} ({selectedInvoiceType.ShortCode})
                </h5>
                <p dangerouslySetInnerHTML={renderHtml(selectedInvoiceType.Description)} />
              </div>
              <h5 className="mt-5">
                {selectedChargeType.Title} ({selectedChargeType.ShortCode})
              </h5>
              <table className="table table-default">
                <thead>
                  <tr>
                    <th>
                      <div>
                        <span>{labels.shortCode}</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>{labels.title}</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>{labels.description}</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>{labels.parentInvoiceType}</span>
                      </div>
                    </th>
                    <th>
                      <div>
                        <span>{labels.taxExempt}</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="card-content">
                  <tr>
                    <td data-title={labels.shortCode}>{selectedChargeType.ShortCode}</td>
                    <td data-title={labels.title}>{selectedChargeType.Title}</td>
                    <td
                      data-title={labels.description}
                      dangerouslySetInnerHTML={renderHtml(selectedChargeType.Description)}
                    />
                    <td data-title={labels.parentInvoiceType}>
                      {selectedInvoiceType.Title} ({selectedInvoiceType.ShortCode})
                    </td>
                    <td data-title={labels.taxExempt}>{selectedChargeType.TaxExempt}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="container my-5">
              <h3 className="sorry">{labels.emptyState}</h3>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
